import { api } from './client';
import type { Authority } from './mrn';

export type PrStatus = 'Draft' | 'Pending' | 'Approved' | 'RFQ Sent' | 'PO Generated' | 'Cancelled';

export interface PrItem {
  id?: number;
  itemId?: number | null;
  itemName: string;
  qty: number | string;
  unit: string;
  unitPrice?: number | string;
  total?: number | string;
}

export interface Pr {
  id: number;
  prNo: string;
  prDate: string;
  /** Short subject, carried forward MRN → PR → PO → GRN. */
  subject?: string | null;
  mrnId: number | null;
  mrn?: { mrnNo: string } | null;
  vendorId: number | null;
  vendor?: { id: number; name: string } | null;
  department: string;
  authority: Authority;
  status: PrStatus;
  remarks: string | null;
  totalValue: string;
  items: PrItem[];
  sla?: {
    state: 'ok' | 'warn' | 'overdue' | 'inactive';
    stepId: string;
    hours: number;
    elapsedHours: number;
    pct: number;
  } | null;
}

export interface PrFilter {
  status?: string;
  dept?: string;
  search?: string;
  /** PR date window (YYYY-MM-DD). */
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
  /** When true, only return PRs that have no RFQ generated yet */
  noRfq?: boolean;
}

export interface PrListResult {
  data: Pr[];
  total: number;
  page: number;
  pageSize: number;
}

export async function listPrs(filter: PrFilter = {}): Promise<PrListResult> {
  const r = await api.get<PrListResult>('/pr', { params: filter });
  return r.data;
}

export async function getPr(id: number): Promise<Pr> {
  const r = await api.get<Pr>(`/pr/${id}`);
  return r.data;
}

export async function createPr(payload: Partial<Pr> & { items: PrItem[] }): Promise<Pr> {
  const r = await api.post<Pr>('/pr', payload);
  return r.data;
}

export async function updatePr(id: number, payload: Partial<Pr>): Promise<Pr> {
  const r = await api.patch<Pr>(`/pr/${id}`, payload);
  return r.data;
}

export async function deletePr(id: number): Promise<void> {
  await api.delete(`/pr/${id}`);
}

export async function approvePr(id: number): Promise<Pr> {
  const r = await api.post<Pr>(`/pr/${id}/approve`);
  return r.data;
}

export async function cancelPr(id: number, reason: string): Promise<Pr> {
  const r = await api.post<Pr>(`/pr/${id}/cancel`, { reason });
  return r.data;
}

export interface FromShortagePayload {
  department: string;
  authority: Authority;
  mrnId?: number;
  subject?: string;
  items: PrItem[];
}

export async function prFromShortage(payload: FromShortagePayload): Promise<Pr> {
  const r = await api.post<Pr>('/pr/from-shortage', payload);
  return r.data;
}
